import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { useMergeCandidates, useMergeStudies } from '@/hooks/use-studies';
import { toast } from '@/hooks/use-toast';
import { trackStudyAction } from '@/lib/analytics/viewer';
import { ApiError } from '@/lib/api/client';
import { NUMERIC_DATE, formatDate } from '@/lib/format';
import { GitMerge, Layers, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, LoadingState, SelectableRow, SelectionToolbar } from './SelectableRow';
const WARNING_KEY = {
    patient_name_differs: 'merge.warning.patientName',
    owner_differs: 'merge.warning.owner',
    species_differs: 'merge.warning.species',
    modality_differs: 'merge.warning.modality',
};
const BLOCKER_KEY = {
    transfer_in_flight: 'merge.blocked.transferInFlight',
    open_consultation_case: 'merge.blocked.openCase',
    draft_report_conflict: 'merge.blocked.draftConflict',
    different_bucket: 'merge.blocked.differentBucket',
    dicom_edit_in_progress: 'merge.blocked.dicomEditInProgress',
};
const CONFLICT_KEY = {
    source_not_found: 'merge.error.sourceNotFound',
    patient_id_mismatch: 'merge.error.patientIdMismatch',
    transfer_in_flight: 'merge.error.transferInFlight',
    open_consultation_case: 'merge.error.openCase',
    draft_report_conflict: 'merge.error.draftConflict',
    different_bucket: 'merge.error.differentBucket',
    dicom_edit_in_progress: 'merge.error.dicomEditInProgress',
};
/**
 * The translation key for a failed merge. A 409 carries a `MergeStudiesConflict`
 * body naming the rule that refused it; anything else is the generic message.
 */
export function mergeErrorKey(error) {
    if (error instanceof ApiError && error.status === 409) {
        try {
            const body = JSON.parse(error.message);
            if (body.code && body.code in CONFLICT_KEY)
                return CONFLICT_KEY[body.code];
        }
        catch {
            // Not JSON: fall through to the generic message.
        }
    }
    return 'merge.error.generic';
}
export function MergeStudiesDialog({ study, open, onOpenChange }) {
    // The body mounts only while open, so its ticks and confirm step start fresh
    // every time: a dialog that remembered last time's selection would merge the
    // wrong studies once the list changed underneath it.
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: open && _jsx(MergeStudiesBody, { study: study, onOpenChange: onOpenChange }) }));
}
function MergeStudiesBody({ study, onOpenChange, }) {
    const { t } = useTranslation();
    const { data, isLoading } = useMergeCandidates(study.id, true);
    const merge = useMergeStudies();
    const [selected, setSelected] = useState(() => new Set());
    const [confirming, setConfirming] = useState(false);
    const candidates = useMemo(() => data?.candidates ?? [], [data]);
    const selectable = useMemo(() => candidates.filter((c) => c.blockers.length === 0), [candidates]);
    // A merge may carry at most one top-level draft report across target and sources. When
    // the target holds one, draft-bearing candidates are already blocked by the server; when
    // it does not, the first draft-bearing candidate ticked takes the slot and the others
    // are held back until it is unticked.
    const draftHolderId = useMemo(() => candidates.find((c) => c.hasDraftReport && selected.has(c.id))?.id, [candidates, selected]);
    const selectAllIds = useMemo(() => {
        const firstDraftId = selectable.find((c) => c.hasDraftReport)?.id;
        return selectable.filter((c) => !c.hasDraftReport || c.id === firstDraftId).map((c) => c.id);
    }, [selectable]);
    const hasPatientId = !!study.patient.id?.trim();
    const toggle = (id) => {
        setConfirming(false);
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const submit = () => {
        const sourceStudyIds = [...selected];
        merge.mutate({ targetId: study.id, sourceStudyIds }, {
            onSuccess: (result) => {
                trackStudyAction('study.merged', { sourceCount: result.mergedStudyIds.length });
                toast({ title: t('merge.success', { count: result.mergedStudyIds.length }) });
                onOpenChange(false);
            },
            onError: (error) => {
                setConfirming(false);
                toast({
                    title: t('merge.failed'),
                    description: t(mergeErrorKey(error)),
                    variant: 'destructive',
                });
            },
        });
    };
    const patientName = study.patient.name?.trim() || t('worklist.unknown');
    return (_jsxs(DialogContent, { className: "max-h-[85dvh] overflow-y-auto sm:max-w-lg", "data-testid": "merge-studies-dialog", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [_jsx(GitMerge, { className: "h-5 w-5" }), t('merge.title')] }), _jsx(DialogDescription, { children: t('merge.description', { patient: patientName, patientId: study.patient.id ?? '' }) })] }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "rounded-md border border-[#2a2f36] bg-[#15181c] px-3 py-2 text-sm", children: [_jsx("span", { className: "text-muted-foreground text-xs", children: t('merge.target') }), _jsx("div", { className: "text-foreground truncate", children: study.study.description?.trim() || t('merge.untitledStudy') }), _jsx("div", { className: "text-muted-foreground truncate text-xs", children: [study.study.modality, formatDate(study.study.dateTime, NUMERIC_DATE)]
                                    .filter(Boolean)
                                    .join(' · ') })] }), !hasPatientId ? (_jsx(EmptyState, { message: t('merge.noPatientId') })) : isLoading ? (_jsx(LoadingState, {})) : candidates.length === 0 ? (_jsx(EmptyState, { message: t('merge.empty') })) : (_jsxs("div", { children: [_jsx(SelectionToolbar, { count: selected.size, total: selectAllIds.length, onSelectAll: () => {
                                    setConfirming(false);
                                    setSelected(new Set(selectAllIds));
                                }, onClear: () => {
                                    setConfirming(false);
                                    setSelected(new Set());
                                } }), _jsx("ul", { className: "space-y-2", children: candidates.map((candidate) => (_jsx(CandidateRow, { candidate: candidate, selected: selected.has(candidate.id), draftHeldElsewhere: candidate.hasDraftReport &&
                                        draftHolderId !== undefined &&
                                        draftHolderId !== candidate.id, onToggle: () => toggle(candidate.id) }, candidate.id))) })] })), confirming && (_jsx("p", { className: "text-destructive text-sm", "data-testid": "merge-confirm-prompt", children: t('merge.confirmPrompt', { count: selected.size }) })), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "ghost", onClick: () => onOpenChange(false), disabled: merge.isPending, children: t('button.cancel') }), confirming ? (_jsxs(Button, { variant: "destructive", onClick: submit, disabled: merge.isPending, "data-testid": "merge-confirm", children: [merge.isPending && _jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }), t('merge.confirm')] })) : (_jsxs(Button, { onClick: () => setConfirming(true), disabled: selected.size === 0, "data-testid": "merge-submit", children: [_jsx(GitMerge, { className: "mr-2 h-4 w-4" }), t('merge.submit', { count: selected.size })] }))] })] })] }));
}
function CandidateRow({ candidate, selected, draftHeldElsewhere, onToggle, }) {
    const { t } = useTranslation();
    const blocked = candidate.blockers.length > 0 || draftHeldElsewhere;
    const blockedReason = candidate.blockers.length > 0
        ? candidate.blockers.map((b) => t(BLOCKER_KEY[b])).join(' · ')
        : t('merge.blocked.anotherDraftSelected');
    return (_jsx(SelectableRow, { selected: selected, onToggle: onToggle, icon: _jsx(Layers, { className: "h-4 w-4" }), title: candidate.description?.trim() || t('merge.untitledStudy'), subtitle: [
            candidate.modality,
            formatDate(candidate.dateTime, NUMERIC_DATE),
            t('merge.seriesImages', { series: candidate.seriesCount, images: candidate.imageCount }),
        ]
            .filter(Boolean)
            .join(' · '), note: candidate.warnings.map((w) => t(WARNING_KEY[w])).join(' · ') || undefined, disabled: blocked, disabledReason: blockedReason, testId: `merge-candidate-${candidate.id}` }));
}
